import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { AuthMe } from "@icraftx/shared/v1";
import { Button } from "@/components/ui/button";
import { PendingStatus } from "@/components/ui/pending-indicator";
import { fetchMe } from "@/shared/api/auth";
import { fetchDeskGlance, type DeskGlance } from "@/shared/api/desk";
import { CollectAgingWidget } from "../CollectAgingWidget";
import { EmptyState } from "../EmptyState";
import { HomeWidget } from "../HomeWidget";
import { HomeCustomizePanel } from "../HomeCustomizePanel";
import {
  defaultHomeWidgetPrefs,
  loadHomeWidgetPrefs,
  saveHomeWidgetPrefs,
  visibleHomeWidgetOrder,
  type HomeMainWidgetId,
  type HomeWidgetPrefs,
} from "../home-widget-prefs";
import { OwnerGlanceBand } from "../OwnerGlanceBand";
import { PageFrame } from "../PageFrame";
import { PageHeader } from "../PageHeader";
import { PayAgingWidget } from "../PayAgingWidget";
import { SalesTrendWidget } from "../SalesTrendWidget";
import { MyTodosWidget } from "../MyTodosWidget";
import { TopCollectWidget } from "../TopCollectWidget";
import { TopLowStockWidget } from "../TopLowStockWidget";
import { TopProductsWidget } from "../TopProductsWidget";
import {
  canSeeCompanyNav,
  companyNavForPermissions,
  shopDayHint,
} from "../nav-math";
import { hasActionPermission } from "../permission-client";
import { loadRecentDocs, recentDocHint } from "../recent-docs";
import { useShellBadges } from "../useShellBadges";

export function CompanyHomePage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState<AuthMe | null>(null);
  const [recentTick, setRecentTick] = useState(0);
  const [glance, setGlance] = useState<DeskGlance | null>(null);
  const [glanceLoading, setGlanceLoading] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [customizeFocus, setCustomizeFocus] =
    useState<HomeMainWidgetId | null>(null);
  const [prefs, setPrefs] = useState<HomeWidgetPrefs>(defaultHomeWidgetPrefs);

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((data) => {
        if (cancelled) return;
        const allowed =
          data.user.isSuperadmin ||
          data.companies.some((c) => c.slug === companySlug);
        if (!allowed) {
          navigate("/select-company", { replace: true });
          return;
        }
        setMe(data);
      })
      .catch(() => {
        if (!cancelled) navigate("/login", { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, [companySlug, navigate]);

  useEffect(() => {
    setRecentTick((n) => n + 1);
  }, [companySlug]);

  useEffect(() => {
    if (!companySlug) return;
    setPrefs(loadHomeWidgetPrefs(companySlug));
  }, [companySlug]);

  useEffect(() => {
    if (!companySlug || !me) return;
    const ac = new AbortController();
    setGlanceLoading(true);
    void fetchDeskGlance(companySlug, ac.signal)
      .then((data) => {
        if (!ac.signal.aborted) setGlance(data);
      })
      .catch(() => {
        if (!ac.signal.aborted) setGlance(null);
      })
      .finally(() => {
        if (!ac.signal.aborted) setGlanceLoading(false);
      });
    return () => ac.abort();
  }, [companySlug, me]);

  const company = me?.companies.find((c) => c.slug === companySlug);
  const held =
    me?.user.isSuperadmin && !company?.permissions?.length
      ? (["*"] as string[])
      : (company?.permissions ?? []);
  const effectiveHeld = held.length
    ? held
    : me?.user.isSuperadmin
      ? (["*"] as string[])
      : [];
  const hint = shopDayHint(effectiveHeld);
  const { primary, more } = companyNavForPermissions(effectiveHeld);
  const {
    itemLabel,
    approvalsPending,
    assignedSo,
    assignedPo,
    queueCounts,
  } = useShellBadges({
    companySlug,
    ready: Boolean(me),
    held: effectiveHeld,
    poll: true,
  });

  const recent = useMemo(() => {
    void recentTick;
    if (!companySlug) return [];
    return loadRecentDocs(companySlug);
  }, [companySlug, recentTick]);

  const canApprove = hasActionPermission(effectiveHeld, "can_approve");
  const showSalesQueues = canSeeCompanyNav(effectiveHeld, "sales");
  const showPurchasingQueues = canSeeCompanyNav(effectiveHeld, "purchasing");
  const showInventory = canSeeCompanyNav(effectiveHeld, "inventory");

  const actItems: { to: string; label: string }[] = [];
  if (companySlug) {
    if (canApprove) {
      actItems.push({
        to: `/c/${companySlug}/approvals`,
        label:
          approvalsPending > 0
            ? `Approvals (${approvalsPending})`
            : "Approvals",
      });
    }
    if (assignedSo > 0) {
      actItems.push({
        to: `/c/${companySlug}/sales/orders?assignee=mine`,
        label: `Assigned sales orders (${assignedSo})`,
      });
    }
    if (assignedPo > 0) {
      actItems.push({
        to: `/c/${companySlug}/purchasing/orders?assignee=mine`,
        label: `Assigned purchase orders (${assignedPo})`,
      });
    }
    if (showSalesQueues) {
      const collect = queueCounts.toCollect || glance?.toCollect?.count || 0;
      const deliver = queueCounts.toDeliver || glance?.toDeliver?.count || 0;
      actItems.push({
        to: `/c/${companySlug}/sales/to-collect`,
        label: collect > 0 ? `To collect (${collect})` : "To collect",
      });
      actItems.push({
        to: `/c/${companySlug}/sales/to-deliver`,
        label: deliver > 0 ? `To deliver (${deliver})` : "To deliver",
      });
    }
    if (showPurchasingQueues) {
      const pay = queueCounts.toPay || glance?.toPay?.count || 0;
      const receive = queueCounts.toReceive || glance?.toReceive?.count || 0;
      actItems.push({
        to: `/c/${companySlug}/purchasing/to-pay`,
        label: pay > 0 ? `To pay (${pay})` : "To pay",
      });
      actItems.push({
        to: `/c/${companySlug}/purchasing/to-receive`,
        label: receive > 0 ? `To receive (${receive})` : "To receive",
      });
    }
  }

  function patchPrefs(next: HomeWidgetPrefs) {
    setPrefs(next);
    if (companySlug) saveHomeWidgetPrefs(companySlug, next);
  }

  if (!me) {
    return (
      <PendingStatus>Loading company…</PendingStatus>
    );
  }

  const taxLabel =
    company?.taxPriceMode === "inclusive"
      ? "Prices include tax"
      : company?.taxPriceMode === "exclusive"
        ? "Tax added at checkout"
        : null;
  const capLabel =
    company != null
      ? `Discounts over ${company.discountSoftCapPct}% need approval`
      : null;
  const meta = company
    ? [company.currencyCode, taxLabel, capLabel].filter(Boolean).join(" · ")
    : "Support view (not a member).";

  const currencyCode = company?.currencyCode ?? "PHP";
  const slug = companySlug!;

  const primaryAction = primary.find((item) => item.id !== "home") ?? null;
  const todayLinks = primary.filter((item) => item.id !== "home");

  function openCustomize(focusId?: HomeMainWidgetId) {
    setCustomizeFocus(focusId ?? null);
    setCustomize(true);
  }

  const widgetBody: Partial<Record<HomeMainWidgetId, ReactNode>> = {
    salesTrend: showSalesQueues ? (
      <SalesTrendWidget
        companySlug={slug}
        currencyCode={currencyCode}
        salesToday={glance?.salesToday}
        loading={glanceLoading}
        onCustomize={() => openCustomize("salesTrend")}
      />
    ) : null,
    collectAging: showSalesQueues ? (
      <CollectAgingWidget
        companySlug={slug}
        currencyCode={currencyCode}
        aging={glance?.toCollectAging}
        loading={glanceLoading}
        onCustomize={() => openCustomize("collectAging")}
      />
    ) : null,
    payAging: showPurchasingQueues ? (
      <PayAgingWidget
        companySlug={slug}
        currencyCode={currencyCode}
        aging={glance?.toPayAging}
        loading={glanceLoading}
        onCustomize={() => openCustomize("payAging")}
      />
    ) : null,
    topProducts: showSalesQueues ? (
      <TopProductsWidget
        companySlug={slug}
        currencyCode={currencyCode}
        rows7={glance?.topProducts}
        rows30={glance?.topProducts30}
        rowsMtd={glance?.topProductsMtd}
        loading={glanceLoading}
        onCustomize={() => openCustomize("topProducts")}
      />
    ) : null,
    topCollect: showSalesQueues ? (
      <TopCollectWidget
        companySlug={slug}
        currencyCode={currencyCode}
        rows={glance?.topToCollect}
        loading={glanceLoading}
        onCustomize={() => openCustomize("topCollect")}
      />
    ) : null,
    topLowStock: showInventory ? (
      <TopLowStockWidget
        companySlug={slug}
        rows={glance?.topLowStock}
        loading={glanceLoading}
        onCustomize={() => openCustomize("topLowStock")}
      />
    ) : null,
    act: (
      <HomeWidget
        title="Act"
        description="Queues, approvals, and work assigned to you. Messages stay on the bell."
        onCustomize={() => openCustomize("act")}
      >
        {actItems.length > 0 ? (
          <ul className="m-0 list-none space-y-2 p-0">
            {actItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="text-sm font-medium text-[var(--color-primary)] no-underline hover:underline"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 text-sm text-[var(--color-muted)]">
            Nothing needs you right now.
          </p>
        )}
      </HomeWidget>
    ),
    myTodos: (
      <MyTodosWidget
        companySlug={slug}
        onCustomize={() => openCustomize("myTodos")}
      />
    ),
    recent: (
      <HomeWidget
        title="Recent"
        description="Documents you opened on this device."
        onCustomize={() => openCustomize("recent")}
      >
        {recent.length > 0 ? (
          <ul className="m-0 list-none space-y-2 p-0">
            {recent.slice(0, 8).map((doc) => (
              <li key={`${doc.kind}-${doc.id}`}>
                <Link
                  to={doc.path}
                  className="text-sm font-medium text-[var(--color-foreground)] no-underline hover:underline"
                >
                  {doc.label}
                </Link>
                <span className="ml-2 text-xs text-[var(--color-muted)]">
                  {doc.hint ?? recentDocHint(doc.kind)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 text-sm text-[var(--color-muted)]">
            Open a document and it will show up here.
          </p>
        )}
      </HomeWidget>
    ),
    today: (
      <HomeWidget
        title="Today"
        description="Where you usually start."
        onCustomize={() => openCustomize("today")}
      >
        {todayLinks.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {todayLinks.map((item) => (
              <Button key={item.id} asChild variant="outline">
                <Link to={item.path(slug)} className="no-underline">
                  {itemLabel(item.id, item.label)}
                </Link>
              </Button>
            ))}
          </div>
        ) : (
          <p className="m-0 text-sm text-[var(--color-muted)]">
            No primary shortcuts for your role yet.
          </p>
        )}
      </HomeWidget>
    ),
    more: (
      <HomeWidget
        title="More"
        description="Settings, books, and quieter tools."
        onCustomize={() => openCustomize("more")}
      >
        {more.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {more.map((item) => (
              <Button key={item.id} asChild variant="outline" size="sm">
                <Link to={item.path(slug)} className="no-underline">
                  {itemLabel(item.id, item.label)}
                </Link>
              </Button>
            ))}
          </div>
        ) : (
          <p className="m-0 text-sm text-[var(--color-muted)]">
            No extra tools for your role yet.
          </p>
        )}
      </HomeWidget>
    ),
  };

  const visibleIds = visibleHomeWidgetOrder(prefs).filter(
    (id) => widgetBody[id] != null,
  );

  return (
    <PageFrame width="desk">
      <PageHeader
        eyebrow="Home"
        title={company?.name ?? companySlug ?? "Company"}
        description={meta}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => openCustomize()}
            >
              Customize
            </Button>
            {primaryAction ? (
              <Button asChild>
                <Link to={primaryAction.path(slug)} className="no-underline">
                  {itemLabel(primaryAction.id, primaryAction.label)}
                </Link>
              </Button>
            ) : null}
          </div>
        }
      />

      {hint ? (
        <p className="mt-6 rounded-[var(--radius-xl)] border border-[var(--color-border)]/90 bg-[var(--color-card)]/90 px-4 py-3.5 text-sm leading-relaxed text-[var(--color-muted)] shadow-[var(--shadow-soft)]">
          {hint}
        </p>
      ) : null}

      <HomeCustomizePanel
        open={customize}
        onOpenChange={(open) => {
          setCustomize(open);
          if (!open) setCustomizeFocus(null);
        }}
        prefs={prefs}
        onPrefsChange={patchPrefs}
        focusId={customizeFocus}
      />

      <div className="mt-6 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,20rem)]">
        <aside className="order-1 lg:sticky lg:top-[calc(var(--desk-sticky-offset)+1rem)] lg:order-2">
          <HomeWidget
            title="Glance"
            description="Shop pulse — numbers open the matching list."
            size="wide"
            className="border-0 bg-transparent p-0 shadow-none"
          >
            <OwnerGlanceBand
              companySlug={slug}
              currencyCode={currencyCode}
              glance={glance}
              loading={glanceLoading}
              canSeeGl={canSeeCompanyNav(effectiveHeld, "trialBalance")}
            />
          </HomeWidget>
        </aside>

        <div className="order-2 flex min-w-0 flex-col gap-8 lg:order-1">
          {visibleIds.map((id) => (
            <div key={id}>{widgetBody[id]}</div>
          ))}

          {primary.length === 0 &&
          more.length === 0 &&
          actItems.length === 0 &&
          !showSalesQueues ? (
            <EmptyState
              title="No modules for this role"
              description="Ask an owner to grant POS, sales, purchasing, or inventory access."
            />
          ) : null}
        </div>
      </div>
    </PageFrame>
  );
}
